/**
 * Keep patient data out of Sentry (browser side).
 *
 * Mirrors backend/src/services/sentryScrub.js — keep the two in step. Every
 * console.error and failed API call becomes a Sentry event (main.jsx,
 * services/api.js), and those carry patient emails, phones and response
 * payloads. Free text is masked, request bodies, cookies and non-allowlisted
 * headers are dropped, and the user is reduced to an id.
 */

export const PATTERNS = [
  // Connection strings with credentials: keep the scheme and host.
  [/\b((?:mongodb(?:\+srv)?|postgres(?:ql)?|redis|amqps?):\/\/)[^\s:@/]+:[^\s@/]+@/gi, '$1[credentials]@'],
  // JWTs, before emails: their segments can look like anything.
  [/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, '[jwt]'],
  [/\b(Bearer|Basic)\s+[\w.~+/=-]{8,}/gi, '$1 [token]'],
  [/[\w.!#$%&'*+/=?^`{|}~-]+@[\w-]+(?:\.[\w-]+)+/g, '[email]'],
  // US phone numbers in the usual shapes; needs a separator or a leading +1
  // so order ids and timestamps are left alone.
  [/(?:\+?1[\s.-]?)?\(?\b\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}\b/g, '[phone]'],
  [/\+1\d{10}\b/g, '[phone]'],
]

export function scrubText(value) {
  if (typeof value !== 'string' || !value) return value
  let out = value
  for (const [pattern, replacement] of PATTERNS) out = out.replace(pattern, replacement)
  return out
}

// Keys whose values are never sent, whatever they hold.
const SECRET_KEY = /pass(word)?|secret|token|authorization|cookie|api[-_]?key|dob|date_?of_?birth|ssn|card|cvv|address|phone|email/i

const MAX_DEPTH = 8

export function scrubValue(value, depth = 0) {
  if (typeof value === 'string') return scrubText(value)
  if (value == null || typeof value !== 'object') return value
  if (depth >= MAX_DEPTH) return '[truncated]'
  if (Array.isArray(value)) return value.map(v => scrubValue(v, depth + 1))
  const out = {}
  for (const [key, v] of Object.entries(value)) {
    out[key] = SECRET_KEY.test(key) && v != null && v !== '' ? '[redacted]' : scrubValue(v, depth + 1)
  }
  return out
}

const SAFE_HEADERS = new Set(['user-agent', 'content-type', 'content-length', 'accept', 'host', 'x-request-id', 'sentry-trace', 'baggage'])

function scrubRequest(request) {
  if (!request) return request
  const out = { ...request }
  delete out.data
  delete out.cookies
  if (out.headers) {
    out.headers = Object.fromEntries(Object.entries(out.headers).filter(([k]) => SAFE_HEADERS.has(k.toLowerCase())))
  }
  if (typeof out.query_string === 'string') out.query_string = scrubText(out.query_string)
  else if (out.query_string) out.query_string = scrubValue(out.query_string)
  if (out.url) out.url = scrubText(out.url)
  return out
}

/** Sentry beforeSend / beforeSendTransaction: returns the event, scrubbed. */
export function scrubEvent(event) {
  if (!event) return event
  if (event.message) event.message = scrubText(event.message)
  if (event.logentry) {
    event.logentry = { ...event.logentry, message: scrubText(event.logentry.message) }
    if (event.logentry.params) event.logentry.params = scrubValue(event.logentry.params)
  }
  for (const ex of event.exception?.values || []) {
    if (ex.value) ex.value = scrubText(ex.value)
    for (const frame of ex.stacktrace?.frames || []) {
      if (frame.vars) frame.vars = scrubValue(frame.vars)
    }
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(b => ({ ...b, message: scrubText(b.message), data: scrubValue(b.data) }))
  }
  if (event.extra) event.extra = scrubValue(event.extra)
  if (event.contexts) event.contexts = scrubValue(event.contexts)
  if (event.tags) event.tags = scrubValue(event.tags)
  if (event.request) event.request = scrubRequest(event.request)
  if (event.transaction) event.transaction = scrubText(event.transaction)
  for (const span of event.spans || []) {
    if (span.description) span.description = scrubText(span.description)
    if (span.data) span.data = scrubValue(span.data)
  }
  if (event.user) event.user = event.user.id != null ? { id: event.user.id } : undefined
  return event
}

/** beforeBreadcrumb: console breadcrumbs carry the same text as console.error. */
export function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb) return breadcrumb
  return { ...breadcrumb, message: scrubText(breadcrumb.message), data: scrubValue(breadcrumb.data) }
}

